import classNames from 'classnames';
import type { SVGProps } from 'react';

import styles from './RevvlyStatusIcon.module.css';

/**
 * What is happening to Revvly, the product, in one glyph:
 * - `idle` up to date; `small` an unread patch release; `major` an unread minor or major release;
 * - `degraded` part of Revvly is slow or failing; `down` Revvly cannot be reached or has no data.
 */
export type RevvlyStatusState = 'idle' | 'small' | 'major' | 'degraded' | 'down';

const WAVE =
  'M1.021 10.5625C4.31511 10.5625 5.13864 2.78709 6.7857 1.15015C8.43276 -0.0775511 8.84453 7.6979 10.0798 13.8364C10.9033 17.5195 12.1386 17.9287 13.3739 15.4733C15.021 12.1995 16.2563 5.24249 17.9033 4.42402C19.1386 4.01479 20.3739 8.51636 22.021 8.51636';

/** The logo's waveform, scaled to sit inside a tile at (x, y) of side s, as the logo's does. */
function Wave({ x, y, s, className }: { x: number; y: number; s: number; className?: string }) {
  const k = (s * 0.78) / 22;
  const tx = x + s / 2 - 11 * k;
  const ty = y + s / 2 - 9.2 * k;
  return (
    <path
      className={classNames(styles.Wave, className)}
      transform={`translate(${tx.toFixed(2)} ${ty.toFixed(2)}) scale(${k.toFixed(3)})`}
      strokeWidth={(2.2 / k).toFixed(2)}
      d={WAVE}
    />
  );
}

/**
 * The Revvly product glyph: the logo tile drawn as an outline in ink, around the logo's waveform.
 * It is deliberately not the AI mark (Brynn: a bare wave with a spark) and never mint, so a
 * product change and the assistant can't be mistaken for each other. See BRAND.md, "Product glyph".
 *
 * Unread releases move instead of showing a dot (the arrow nudges, a new layer draws onto the
 * stack) in short bursts with rests between them. Under `prefers-reduced-motion` the glyph rests
 * and a small dot marks unread instead. Sized by the parent's `svg` rule, like a lucide icon.
 */
export function RevvlyStatusIcon({
  state,
  className,
  ...props
}: { state: RevvlyStatusState } & SVGProps<SVGSVGElement>) {
  const unread = state === 'small' || state === 'major';
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      aria-hidden="true"
      focusable="false"
      data-state={state}
      className={classNames(styles.Icon, styles[state], className)}
      {...props}
    >
      {state === 'small' && (
        <>
          <rect className={styles.Tile} x={3} y={8} width={21} height={21} rx={7} />
          <Wave x={3} y={8} s={21} />
          <path className={styles.Arrow} d="M27.5 14.5V4.2M24.3 7.4l3.2-3.2 3.2 3.2" />
        </>
      )}
      {state === 'major' && (
        <>
          <path className={styles.Layer} pathLength={1} d="M10 4h11a7 7 0 0 1 7 7v11" />
          <g className={styles.Front}>
            <rect className={styles.Tile} x={3} y={9} width={20} height={20} rx={6} />
            <Wave x={3} y={9} s={20} />
          </g>
        </>
      )}
      {(state === 'idle' || state === 'degraded' || state === 'down') && (
        <>
          {state === 'degraded' && (
            <rect className={styles.Pulse} x={4} y={4} width={24} height={24} rx={7} />
          )}
          <rect className={styles.Tile} x={4} y={4} width={24} height={24} rx={7} />
          <Wave x={4} y={4} s={24} className={state === 'down' ? styles.Broken : undefined} />
        </>
      )}
      {unread && <circle className={styles.StillDot} cx={29} cy={3} r={3} />}
    </svg>
  );
}

export default RevvlyStatusIcon;
